import type {
  SupportClientContext,
  SupportClientContextField,
} from '@/types/Support';

/** Ordered — Edge/Opera/Samsung must win before Chrome, Chrome before Safari. */
const BROWSER_MATCHERS: ReadonlyArray<{ name: string; pattern: RegExp }> = [
  { name: 'Edge', pattern: /Edg(?:e|A|iOS)?\/([\d.]+)/ },
  { name: 'Opera', pattern: /(?:OPR|Opera)\/([\d.]+)/ },
  { name: 'Samsung Internet', pattern: /SamsungBrowser\/([\d.]+)/ },
  { name: 'Firefox', pattern: /(?:Firefox|FxiOS)\/([\d.]+)/ },
  { name: 'Chrome', pattern: /(?:Chrome|CriOS)\/([\d.]+)/ },
  { name: 'Safari', pattern: /Version\/([\d.]+).*Safari/ },
];

const OS_MATCHERS: ReadonlyArray<{ name: string; pattern: RegExp }> = [
  { name: 'Windows', pattern: /Windows NT ([\d.]+)/ },
  { name: 'Android', pattern: /Android ([\d.]+)/ },
  { name: 'iOS', pattern: /(?:iPhone|iPad|iPod).*OS ([\d_]+)/ },
  { name: 'macOS', pattern: /Mac OS X ([\d_]+)/ },
  { name: 'Linux', pattern: /(Linux)/ },
];

/** `Chrome 140` — major version only, which is all support needs. */
export function detectBrowser(userAgent: string): string | undefined {
  const match = BROWSER_MATCHERS.find(({ pattern }) => pattern.test(userAgent));
  if (!match) return undefined;

  const version = userAgent.match(match.pattern)?.[1]?.split('.')[0];
  return version ? `${match.name} ${version}` : match.name;
}

/** `macOS 10.15`, `iOS 17.4`, `Windows` — version omitted when unparseable. */
export function detectOperatingSystem(userAgent: string): string | undefined {
  const match = OS_MATCHERS.find(({ pattern }) => pattern.test(userAgent));
  if (!match) return undefined;

  const raw = userAgent.match(match.pattern)?.[1]?.replace(/_/g, '.');
  return raw && raw !== match.name ? `${match.name} ${raw}` : match.name;
}

function correlationId(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Build the `client_context` payload for a support request from the browser.
 * Only the fields the backend advertises for the group are returned, and empty
 * values are dropped so the object never carries blanks.
 *
 * @param screen Route the user was on when raising the request (e.g. `/checkout`).
 * @param allowedFields `client_context_fields` from `GET /support/options`.
 */
export function buildClientContext(
  screen: string,
  allowedFields: SupportClientContextField[]
): SupportClientContext {
  if (!import.meta.client) return {};

  const candidates: SupportClientContext = {
    browser: detectBrowser(navigator.userAgent),
    operating_system: detectOperatingSystem(navigator.userAgent),
    device: `${window.screen.width}x${window.screen.height}`,
    screen,
    correlation_id: correlationId(),
  };

  return Object.fromEntries(
    Object.entries(candidates).filter(
      ([field, value]) =>
        Boolean(value) &&
        allowedFields.includes(field as SupportClientContextField)
    )
  );
}
